/**
 * Metric Card Component
 * Beautiful card to display key metrics
 */

import { motion } from 'framer-motion';
import { ReactNode } from 'react';

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number; // percentage change
  icon?: ReactNode;
  color?: string;
  subtitle?: string;
}

export function MetricCard({ title, value, change, icon, color = 'indigo', subtitle }: MetricCardProps) {
  const colorClasses = {
    indigo: 'bg-indigo-500',
    green: 'bg-green-500',
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    red: 'bg-red-500',
    amber: 'bg-amber-500',
  };

  const bgColor = colorClasses[color as keyof typeof colorClasses] || colorClasses.indigo;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-xl shadow-sm dark:shadow-xl dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-700/50 dark:ring-1 dark:ring-white/5 p-6 hover:shadow-md dark:hover:shadow-2xl transition-all duration-300"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 dark:text-slate-400 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">{subtitle}</p>}
          {change !== undefined && (
            <div className="flex items-center mt-2">
              {change >= 0 ? (
                <span className="text-green-600 dark:text-green-400 text-sm font-medium flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {Math.abs(change).toFixed(1)}%
                </span>
              ) : (
                <span className="text-red-600 dark:text-red-400 text-sm font-medium flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {Math.abs(change).toFixed(1)}%
                </span>
              )}
              <span className="text-xs text-gray-500 dark:text-slate-400 ml-2">vs periodo precedente</span>
            </div>
          )}
        </div>
        {icon && (
          <div className={`${bgColor} rounded-lg p-3 text-white shadow-lg dark:shadow-${color}-900/50`}>
            {icon}
          </div>
        )}
      </div>
    </motion.div>
  );
}
